import React, { FC, useState, useEffect, useContext } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';

import { CustomerIssue } from '../../model';
import axios from '../../../sharedModules/utils/axios.utils';
import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';

interface CustomerIssueUpdateFormProps {
  issue: CustomerIssue;
  onSuccess: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
}

const CustomerIssueUpdateForm: FC<CustomerIssueUpdateFormProps> = ({
  issue,
  onSuccess,
  onCancel,
  showCancelButton = false,
}) => {
  const { user } = useContext(AuthenticationContext);
  const userName = user?.name || user?.last_name_first || `${user?.first_name} ${user?.last_name}` || 'Unknown User';

  const [formValues, setFormValues] = useState({
    DATE_CHNG: format(new Date(), 'MM/dd/yyyy'),
    TIME_CHNG: format(new Date(), 'HH:mm:ss'),
    CHNGED_BY: userName,
    CMPLT_DATE: '',
    CMPLT_FLAG: 'N',
    U_RSDISP: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (issue) {
      setFormValues({
        DATE_CHNG: format(new Date(), 'MM/dd/yyyy'),
        TIME_CHNG: format(new Date(), 'HH:mm:ss'),
        CHNGED_BY: user?.servman_user_id,
        CMPLT_DATE: issue.resolved === 'Y' ? (issue.opened ? format(new Date(issue.opened), 'MM/dd/yyyy') : '') : '',
        CMPLT_FLAG: issue.resolved || 'N',
        U_RSDISP: '',
      });
      setError('');
      setSuccess(false);
    }
  }, [issue, userName]);

  const handleSubmit = async () => {
    if (!issue) return;

    if (!formValues.CHNGED_BY.trim()) {
      setError('Changed By is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let updatedFormValues = { ...formValues };

      const prependedNote = `${formValues.DATE_CHNG} - ${user?.servman_user_id} - ${formValues.U_RSDISP}`;
      const existingNotes = issue.resolution || '';

      if (existingNotes.trim().length > 0) {
        updatedFormValues.U_RSDISP = `${prependedNote}

        ${existingNotes}`;
      } else {
        updatedFormValues.U_RSDISP = `${prependedNote}`;
      }

      await axios.patch(`/customer_issues/servman_customer_issues/${issue.id}`, {
        customer_issue: updatedFormValues,
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      console.log(err);
      setError(err.response?.data?.error || 'Failed to update customer issue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
          Customer issue updated successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Changed By"
            fullWidth
            value={formValues.CHNGED_BY}
            disabled
            helperText="Automatically set to current user"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Status</InputLabel>
            <Select
              value={formValues.CMPLT_FLAG}
              label="Status"
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  CMPLT_FLAG: e.target.value,
                  CMPLT_DATE: e.target.value === 'Y' ? format(new Date(), 'MM/dd/yyyy') : '',
                })
              }
              disabled={loading || success}
            >
              <MenuItem value="N">Open</MenuItem>
              <MenuItem value="Y">Resolved</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="New Notes"
            fullWidth
            multiline
            rows={4}
            value={formValues.U_RSDISP}
            onChange={(e) => setFormValues({ ...formValues, U_RSDISP: e.target.value })}
            disabled={loading || success}
            helperText="New notes will be prepended to the top of previous notes"
          />
        </Grid>

        {issue.resolution && issue.resolution.trim().length > 0 && (
          <Grid item xs={12}>
            <TextField
              label="Previous Notes"
              fullWidth
              multiline
              rows={6}
              value={issue.resolution}
              disabled
              helperText="Historical notes (read-only)"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        )}
      </Grid>

      <Box
        sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}
      >
        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> Changes will be synced to ServMan. Updates may take a few minutes to appear in the
          system.
        </Typography>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {showCancelButton && onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default CustomerIssueUpdateForm;
